import React from 'react';
import { createPortal } from 'react-dom';
import JobEditorPage from './JobEditorPage';
import {
  BookOpen,
  UserPlus,
  UserMinus,
  Search,
  Download,
  Plus,
  Clock,
  Star,
  Award,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar
} from 'lucide-react';
import axios from '../../axios-config'; // Use configured axios with interceptors
import { OrganizationJobsContext } from '../../contexts/OrganizationJobsContext';
import TeacherJobAssignment from './TeacherJobAssignment';

type JobStatus = 'active' | 'inactive' | 'archived';
type StudentStatus = 'active' | 'warning' | 'terminated';
type PaymentStatus = 'scheduled' | 'paused' | 'manual';

type Job = {
  id: string;
  organizationId: string;
  title: string;
  description: string;
  location: string;
  schedule: string;
  points: number;
  requirements: string[];
  skills: string[];
  status: JobStatus;
  supervisor: string;
  maxPositions: number;
  currentPositions: number;
  icon: React.ReactNode;
  deactivatedOn?: Date | null;
  organizationJobAssignments: any;
  organizationJobAssignmentSchedules: any;
};

type StudentJob = {
  id: string;
  organizationId: string;
  jobId: string;
  studentId: string;
  studentName: string;
  studentAvatar: string;
  startDate: string;
  schedule: string;
  totalEarned: number;
  status: StudentStatus;
  performance: number;
  lastShift: string;
  nextShift: string;
  hoursThisWeek: number;
  pointsThisWeek: number;
  paymentStatus: PaymentStatus;
  lastPayment: string;
  nextPayment: string;
  weeklyPoints: number;
  jobEndDate?: string;
};

export default function TeacherJobsManagement({
  activeTab = 'positions'
}: {
  visible?: boolean;
  activeTab?: 'positions' | 'students' | 'applications';
}) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedStatus, setSelectedStatus] = React.useState<JobStatus | 'all'>(
    'all'
  );
  const [showManageStudents, setShowManageStudents] = React.useState(false);
  const [showManagePoints, setShowManagePoints] = React.useState(false);
  const [selectedJob, setSelectedJob] = React.useState<Job | null>(null);
  const [selectedStudent, setSelectedStudent] = React.useState<StudentJob | null>(
    null
  );
  const [pointsToAdd, setPointsToAdd] = React.useState('');
  const [pointsNote, setPointsNote] = React.useState('');
  const [_, setShowIssueWarning] = React.useState(false);
  const [__, setShowPaymentSettings] = React.useState(false);
  const [jobs, setJobs] = React.useState<Job[]>([]);
  const [studentJobs, setStudentJobs] = React.useState<StudentJob[]>([]);

  const [isEditingJob, setIsEditingJob] = React.useState(false);
  const [editingJob, setEditingJob] = React.useState<Job | null>(null);

  // For assignment popup
  const [showJobAssignments, setShowJobAssignments] = React.useState(false);
  const [selectedAssignment, setSelectedAssignment] = React.useState<any>(null);

  const { organizationAssignments, organizationJobs } = React.useContext(
    OrganizationJobsContext
  );

  const normalizeJobsResponse = (data: any): any[] => {
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && Array.isArray((data as any).jobs)) {
      return (data as any).jobs;
    }
    return [];
  };

  // We'll store the selected assignment in stable form to pass as a prop
  const stableAssignmentData = React.useMemo(() => selectedAssignment, [
    selectedAssignment
  ]);

  // Transform the raw job data from back-end into our local "Job" format
  const transformJobs = (data: any[]): Job[] => {
    return Array.isArray(data)
      ? data.map((item: any) => {
          let scheduleStr = `Pay every ${item.payEvery} week(s) on day ${
            item.payDayOfWeek
          } - first pay: ${new Date(item.firstPayDate).toLocaleDateString()}`;

          if (
            Array.isArray(item.organizationJobAssignmentSchedules) &&
            item.organizationJobAssignmentSchedules.length > 0
          ) {
            const dayMap: Record<number, string> = {
              1: 'Mon',
              2: 'Tue',
              3: 'Wed',
              4: 'Thu',
              5: 'Fri',
              6: 'Sat',
              7: 'Sun'
            };
            const days = item.organizationJobAssignmentSchedules
              .map((s: any) => dayMap[s.dayOfWeek])
              .sort((a: string, b: string) => {
                const order = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                return order.indexOf(a) - order.indexOf(b);
              });

            // Convert e.g. "14:00:00" -> "2:00 PM"
            const parseTime = (timeStr: string) => {
              const [hourStr, minuteStr] = timeStr.split(':');
              let hour = parseInt(hourStr, 10);
              const minute = minuteStr;
              const ampm = hour >= 12 ? 'PM' : 'AM';
              hour = hour % 12 || 12;
              return `${hour}:${minute} ${ampm}`;
            };

            const startTime = parseTime(
              item.organizationJobAssignmentSchedules[0].startTime
            );
            const endTime = parseTime(
              item.organizationJobAssignmentSchedules[0].endTime
            );
            scheduleStr = `${days.join(', ')} • ${startTime} - ${endTime}`;
          }

          return {
            id: item.organizationJobId.toString(),
            organizationId: item.organizationId.toString(),
            title: item.jobName,
            description: item.jobDescription,
            location: item.jobLocation,
            schedule: scheduleStr,
            points: item.jobPoints,
            requirements: Array.isArray(item.requirements)
              ? item.requirements
              : [],
            skills: Array.isArray(item.skills) ? item.skills : [],
            status: item.deactivatedOn
              ? ('inactive' as JobStatus)
              : ('active' as JobStatus),
            supervisor: item.supervisorId,
            maxPositions: item.maxPositions,
            currentPositions: item.currentPositions,
            icon: <BookOpen className="w-5 h-5" />,
            deactivatedOn: item.deactivatedOn ? new Date(item.deactivatedOn) : null,
            organizationJobAssignments: item.organizationJobAssignments,
            organizationJobAssignmentSchedules:
              item.organizationJobAssignmentSchedules
          };
        })
      : [];
  };

  const refreshJobs = async () => {
    try {
      if (organizationJobs && organizationJobs.length > 0) {
        setJobs(transformJobs(organizationJobs));
      } else {
        const res = await axios.get('/api/OrganizationJobs');
        setJobs(transformJobs(normalizeJobsResponse(res.data)));
      }
    } catch (error) {
      console.error('Error fetching OrganizationJobs:', error);
    }
  };

  React.useEffect(() => {
    refreshJobs();
  }, []);

  React.useEffect(() => {
    if (organizationAssignments) {
      setStudentJobs(organizationAssignments);
    }
  }, [organizationAssignments]);

  React.useEffect(() => {
    // If the parent context changes the job list, let's refresh our local list too
    refreshJobs();
  }, [organizationJobs]);

  // Filter the job cards
  const filteredJobs = jobs.filter((job) => {
    const effectiveStatus =
      job.deactivatedOn && job.deactivatedOn < new Date()
        ? 'inactive'
        : job.status;

    const matchesSearch =
      searchTerm.trim() === '' ||
      job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      selectedStatus === 'all' || effectiveStatus === selectedStatus;

    return matchesSearch && matchesStatus;
  });

  // Example placeholders for managing student actions
  const handleAddPoints = (studentId: string) => {
    setSelectedStudent(studentJobs.find((s) => s.id === studentId) || null);
    setShowManagePoints(true);
  };
  const handleChangePosition = (studentId: string) => {
    console.log('Removing student from position', studentId);
    setShowManageStudents(false);
  };
  const handleManagePayments = (studentId: string) => {
    setSelectedStudent(studentJobs.find((s) => s.id === studentId) || null);
    setShowPaymentSettings(true);
  };
  const handleIssueWarning = (studentId: string) => {
    setSelectedStudent(studentJobs.find((s) => s.id === studentId) || null);
    setShowIssueWarning(true);
  };

  // small helpers for status display
  const getStatusColor = (status: JobStatus | StudentStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-50 text-green-600';
      case 'inactive':
        return 'bg-gray-50 text-gray-600';
      case 'archived':
        return 'bg-yellow-50 text-yellow-600';
      case 'warning':
        return 'bg-orange-50 text-orange-600';
      case 'terminated':
        return 'bg-red-50 text-red-600';
      default:
        return 'bg-gray-50 text-gray-600';
    }
  };
  const getStatusIcon = (status: JobStatus | StudentStatus) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4" />;
      case 'terminated':
        return <XCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // Job Editing
  const handleEditJob = (job: Job) => {
    setEditingJob(job);
    setIsEditingJob(true);
  };
  const handleCreateJob = () => {
    setEditingJob(null);
    setIsEditingJob(true);
  };
  const handleBackFromJobEdit = () => {
    setIsEditingJob(false);
    setEditingJob(null);
  };
  const handleJobSuccess = () => {
    setIsEditingJob(false);
    setEditingJob(null);
    refreshJobs();
  };

  // Student assignment editing
  const handleAssignmentUpdate = (updatedItem: any) => {
    setSelectedAssignment(updatedItem);
  };

  const handleEditStudentAssignment = (student: any) => {
    setSelectedAssignment(student);
    setShowJobAssignments(true);
  };
  const handleCreateStudentAssignment = () => {
    setSelectedAssignment(null);
    setShowJobAssignments(true);
  };

  return (
    <>
      {activeTab === 'positions' && (
        <div className="space-y-6">
          {!isEditingJob ? (
            <>
              <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 p-4">
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                  <div className="flex items-center gap-4 w-full sm:w-auto">
                    <div className="relative flex-1 sm:flex-none">
                      <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search positions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 bg-white/50 border border-gray-200 rounded-lg w-full sm:w-64 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                      />
                    </div>
                    <select
                      value={selectedStatus}
                      onChange={(e) =>
                        setSelectedStatus(e.target.value as JobStatus | 'all')
                      }
                      className="px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <button className="px-4 py-2 bg-white/50 text-gray-700 rounded-lg hover:bg-white transition-colors flex items-center gap-2 w-full sm:w-auto justify-center">
                      <Download className="w-4 h-4" />
                      Export
                    </button>
                    {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                      <button
                        onClick={() => {}}
                        className="text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        Issue Warning
                      </button>
                    )}
                    <button
                      onClick={handleCreateJob}
                      className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
                    >
                      <Plus className="w-4 h-4" />
                      Add Position
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredJobs.map((job) => (
                  <div
                    key={job.id}
                    className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-gray-50 rounded-lg">{job.icon}</div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-800">
                              {job.title}
                            </h3>
                            <p className="text-sm text-gray-500">{job.location}</p>
                          </div>
                        </div>
                        <span
                          className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(
                            job.status
                          )}`}
                        >
                          {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                        </span>
                      </div>

                      <p className="text-gray-600 text-sm mb-4">
                        {job.description}
                      </p>

                      <div className="space-y-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          {job.schedule}
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Points per shift</span>
                          <span className="font-medium text-gray-800">
                            {job.points}
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Positions</span>
                          <span className="font-medium text-gray-800">
                            {job.currentPositions} / {job.maxPositions}
                          </span>
                        </div>

                        <div className="space-y-2">
                          <p className="text-sm font-medium text-gray-700">
                            Requirements:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {job.requirements.map((req, index) => (
                              <span
                                key={index}
                                className="bg-gray-100 text-gray-600 px-2 py-1 rounded-lg text-xs"
                              >
                                {req}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <p className="text-sm font-medium text-gray-700">
                            Skills:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {job.skills.map((skill, index) => (
                              <span
                                key={index}
                                className="bg-blue-50 text-blue-600 px-2 py-1 rounded-lg text-xs"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="pt-4 flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedJob(job);
                              setShowManageStudents(true);
                            }}
                            className="flex-1 bg-green-50 text-green-600 px-4 py-2 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
                          >
                            Manage Students
                          </button>
                          <button
                            onClick={() => handleEditJob(job)}
                            className="flex-1 bg-indigo-50 text-indigo-600 px-4 py-2 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            className="flex-1 bg-red-50 text-red-600 px-4 py-2 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
                          >
                            Archive
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <JobEditorPage
              editItem={
                editingJob
                  ? {
                      organizationJobId: editingJob.id,
                      organizationId: editingJob.organizationId,
                      jobName: editingJob.title,
                      jobDescription: editingJob.description,
                      jobPoints: editingJob.points,
                      payEvery:
                        editingJob.organizationJobAssignments?.[0]?.payEvery || 1,
                      payDayOfWeek:
                        editingJob.organizationJobAssignments?.[0]
                          ?.payDayOfWeek || 1,
                      firstPayDate:
                        editingJob.organizationJobAssignments?.[0]?.firstPayDate ||
                        new Date().toISOString().split('T')[0],
                      jobLocation: editingJob.location,
                      maxPositions: editingJob.maxPositions,
                      skills: editingJob.skills,
                      requirements: editingJob.requirements,
                      deactivatedOn: editingJob.deactivatedOn,
                      organizationJobAssignmentSchedules:
                        editingJob.organizationJobAssignmentSchedules
                    }
                  : null
              }
              onBack={handleBackFromJobEdit}
              onSuccess={handleJobSuccess}
            />
          )}
        </div>
      )}

      {activeTab === 'students' && (
        <>
          <div className="space-y-6">
            {!showJobAssignments && (
              <>
                <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 p-4">
                  <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                      <div className="relative flex-1 sm:flex-none">
                        <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          placeholder="Search students..."
                          className="pl-10 pr-4 py-2 bg-white/50 border border-gray-200 rounded-lg w-full sm:w-64 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                        />
                      </div>
                      <select className="px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors">
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="warning">Warning</option>
                        <option value="terminated">Terminated</option>
                      </select>
                    </div>
                    <button className="px-4 py-2 bg-white/50 text-gray-700 rounded-lg hover:bg-white transition-colors flex items-center gap-2 w-full sm:w-auto justify-center">
                      <Download className="w-4 h-4" />
                      Export Report
                    </button>
                  </div>
                </div>

                <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 overflow-hidden relative">
                  {/* <div className="p-4 border-b border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-800">
                      Student Workers
                    </h2>
                  </div> */}
                  <div className="divide-y divide-gray-100 relative">
                    {studentJobs.map((student) => (
                      <div
                        key={student.id}
                        className="p-6 hover:bg-indigo-50/50 transition-colors cursor-pointer relative"
                        onClick={() => handleEditStudentAssignment(student)}
                      >
                        <div className="flex flex-col sm:flex-row justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <img
                              src={student.studentAvatar}
                              alt={student.studentName}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                            <div>
                              <div className="flex items-center gap-3">
                                <h3 className="font-medium text-gray-800">
                                  {student.studentName}
                                </h3>
                                <span
                                  className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(
                                    student.status
                                  )}`}
                                >
                                  {getStatusIcon(student.status)}
                                  {student.status
                                    ?.charAt(0)
                                    .toUpperCase() + student.status?.slice(1)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-500">
                                Started {new Date(student.startDate).toLocaleDateString()}
                                {student.jobEndDate ? ` (Ends: ${student.jobEndDate.split('T')[0]})` : ''}
                              </p>
                              <p className="text-sm text-gray-500">
                                {student.schedule}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <div className="text-right">
                              <p className="font-medium text-gray-800">
                                {student.totalEarned} pts earned
                              </p>
                              {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                              <div className="flex items-center gap-1 text-sm text-yellow-500">
                                <Star className="w-4 h-4" />
                                <span>{student.performance?.toFixed(1)}</span>
                              </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 pl-16">
                          <div className="bg-gray-50/50 rounded-lg p-4">
                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                              <div>
                                <p className="text-sm text-gray-500">
                                  Hours This Week
                                </p>
                                <p className="font-medium text-gray-800">
                                  {student.hoursThisWeek}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">
                                  Points This Week
                                </p>
                                <p className="font-medium text-gray-800">
                                  {student.pointsThisWeek}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">
                                  Last Shift
                                </p>
                                <p className="font-medium text-gray-800">
                                  {new Date(student.lastShift).toLocaleDateString()}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">
                                  Next Shift
                                </p>
                                <p className="font-medium text-gray-800">
                                  {new Date(student.nextShift).toLocaleDateString()}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 mt-4">
                              <button className="px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-colors">
                                View Schedule
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedStudent(student);
                                  setShowManagePoints(true);
                                }}
                                className="px-3 py-1.5 bg-green-50 text-green-600 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors"
                              >
                                Add Points
                              </button>
                              <button className="px-3 py-1.5 bg-yellow-50 text-yellow-600 rounded-lg text-sm font-medium hover:bg-yellow-100 transition-colors">
                                Performance Review
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedStudent(student);
                                  setShowPaymentSettings(true);
                                }}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                  student.paymentStatus === 'scheduled'
                                    ? 'bg-green-50 text-green-600 hover:bg-green-100'
                                    : 'bg-red-50 text-red-600 hover:bg-red-100'
                                }`}
                              >
                                {student.paymentStatus === 'scheduled'
                                  ? 'Payments Active'
                                  : 'Payments Paused'}
                              </button>
                              {student.status === 'active' && (
                                <button className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors">
                                  Issue Warning
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="absolute top-2 right-2 bg-indigo-500 text-white text-xs px-2 rounded">
                          Edit
                        </div>
                      </div>
                    ))}
                    <div
                      className="p-6 hover:bg-indigo-50/50 transition-colors cursor-pointer flex items-center justify-center"
                      onClick={handleCreateStudentAssignment}
                    >
                      <div className="flex flex-col items-center">
                        <UserPlus className="w-6 h-6 mb-2 text-indigo-500" />
                        <span className="text-indigo-700 font-medium">
                          Add New Assignment
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* The assignment editing pop-up */}
          <div className={showJobAssignments ? '' : 'hidden'}>
            <TeacherJobAssignment
              assignmentData={stableAssignmentData}
              onBack={() => {
                setShowJobAssignments(false);
                setSelectedAssignment(null);
              }}
              onAssignmentUpdate={handleAssignmentUpdate}
            />
          </div>
        </>
      )}

      {activeTab === 'applications' && (
        <div className="space-y-6">
          <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 p-4">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="flex items-center gap-4 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search applications..."
                    className="pl-10 pr-4 py-2 bg-white/50 border border-gray-200 rounded-lg w-full sm:w-64 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  />
                </div>
                <select className="px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors">
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">
                Recent Applications
              </h2>
            </div>
            <div className="p-6 text-center text-gray-500">
              <Award className="w-12 h-12 mx-auto text-gray-400 mb-2" />
              <p>No pending applications</p>
            </div>
          </div>
        </div>
      )}

      {/* Manage Students Modal */}
      {showManageStudents &&
        selectedJob &&
        createPortal(
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
            <div className="bg-white/90 backdrop-blur-md p-6 rounded-2xl shadow-xl w-full max-w-2xl">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    {selectedJob.title}
                  </h3>
                  <p className="text-sm text-gray-500">{selectedJob.location}</p>
                </div>
                <button
                  onClick={() => setShowManageStudents(false)}
                  className="p-2 hover:bg-gray-100/80 rounded-full transition-colors"
                >
                  <XCircle className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="bg-gray-50/50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {selectedJob.schedule}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {selectedJob.currentPositions}/{selectedJob.maxPositions} positions filled
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                {studentJobs
                  .filter((student) => String(student.jobId) === selectedJob.id)
                  .map((student) => (
                    <div
                      key={student.id}
                      className="bg-white border border-gray-100 rounded-xl p-4"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={student.studentAvatar}
                            alt={student.studentName}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-gray-800">
                                {student.studentName}
                              </h4>
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  student.status === 'active'
                                    ? 'bg-green-50 text-green-600'
                                    : 'bg-orange-50 text-orange-600'
                                }`}
                              >
                                {student.status === 'active'
                                  ? 'Active'
                                  : 'Warning'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500">
                              {student.schedule === 'N/A'
                                ? selectedJob.schedule
                                : student.schedule}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1 text-sm">
                            <Star className="w-4 h-4 text-yellow-400" />
                            <span className="font-medium">
                              {student.performance}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">
                            {student.pointsThisWeek} pts weekly
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleAddPoints(student.id)}
                          className="px-3 py-1.5 bg-green-50 text-green-600 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors"
                        >
                          Add Points
                        </button>
                        <button
                          onClick={() => handleChangePosition(student.id)}
                          className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors flex items-center gap-1"
                        >
                          <UserMinus className="w-4 h-4" />
                          Let Go
                        </button>
                        <button
                          onClick={() => handleManagePayments(student.id)}
                          className="px-3 py-1.5 bg-purple-50 text-purple-600 rounded-lg text-sm font-medium hover:bg-purple-100 transition-colors"
                        >
                          {student.paymentStatus === 'scheduled'
                            ? 'Manage Payments'
                            : 'Payments Paused'}
                        </button>
                        {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                          <button
                            onClick={() => {}}
                            className="px-3 py-1.5 bg-violet-50 text-violet-600 rounded-lg text-sm font-medium hover:bg-violet-100 transition-colors"
                          >
                            Performance Review
                          </button>
                        )}
                        {student.status === 'active' && (
                          <button
                            onClick={() => handleIssueWarning(student.id)}
                            className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors"
                          >
                            Issue Warning
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>

              {selectedJob.currentPositions < selectedJob.maxPositions && (
                <button className="mt-4 w-full bg-gray-800 text-white px-4 py-3 rounded-xl hover:bg-gray-900 transition-colors flex items-center justify-center gap-2 font-medium">
                  <UserPlus className="w-5 h-5" />
                  Add Student
                </button>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowManageStudents(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Manage Points Modal */}
      {showManagePoints && selectedStudent && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4">
          <div className="bg-white/90 backdrop-blur-md p-8 rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-800">
                Add Points - {selectedStudent.studentName}
              </h3>
              <button
                onClick={() => setShowManagePoints(false)}
                className="p-2 hover:bg-gray-100/80 rounded-full transition-colors"
              >
                <XCircle className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <form className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Points to Add
                </label>
                <input
                  type="number"
                  value={pointsToAdd}
                  onChange={(e) => setPointsToAdd(e.target.value)}
                  className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  placeholder="Enter points"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  value={pointsNote}
                  onChange={(e) => setPointsNote(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  placeholder="Add a note about these points..."
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-indigo-500 text-white px-4 py-2 rounded-lg hover:bg-indigo-600 transition-colors text-sm font-medium"
                >
                  Add Points
                </button>
                <button
                  type="button"
                  onClick={() => setShowManagePoints(false)}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}